import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useState } from 'react';

import {
    Box,
    Button,
    Flex,
    GridItem,
    HStack,
    Link,
    Menu,
    Text,
    useMediaQuery,
    VStack,
} from '@chakra-ui/react';

import { urlHelpers } from '@nocowanie/core';
import { Avatar, GridWrapper, searchEngineIsMobileSsrBreakpoint } from '@nocowanie/common-ui';
import { IconsLinear } from '@nocowanie/icons';

import { SearchEngineWrapper } from '@app/components/molecules/search-engine-wrapper';
import { Navigation, NavigationMobile } from '@app/components/organisms/navigation';
import { MarketingBanner } from '@app/esi-components';
import { getDashboardLink, logOutUser } from '@app/helpers/navigation.helpers';
import { useUserProfile } from '@app/hooks';
import { NavGroupModel } from '@app/models';

import { useAppHeaderHook } from './app-header.hook';
import { AppHeaderProps } from './app-header.props';
import { AppHeaderLogo } from './app-header-logo';

import staticData from '../../../assets/data/static-data.json';
import navLinks from '../navigation/navigation-links.json';

export const AppHeader = ({
    isSimplified = true,
    showSearchEngine = false,
    isHomePage = false,
    isV7Theme = false,
    bannerData = null,
}: AppHeaderProps) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'appHeader',
    });
    const { isMobileRes } = useAppHeaderHook();
    const isMobileHomepage = isHomePage && isMobileRes;
    const isGhostVariant = isMobileHomepage && isV7Theme;
    const isOldMobileVariant = isMobileHomepage && !isV7Theme;
    const { user } = useUserProfile();
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const handleLogout = useCallback(() => {
        logOutUser();
    }, []);

    const handleCloseProfile = useCallback(() => {
        setIsProfileOpen(false);
    }, []);

    const handleOpenProfile = useCallback(() => {
        setIsProfileOpen(true);
    }, []);

    useEffect(() => {
        if (!isProfileOpen) {
            return;
        }

        window.addEventListener('scroll', handleCloseProfile);

        return () => {
            window.removeEventListener('scroll', handleCloseProfile);
        };
    }, [handleCloseProfile, isProfileOpen]);

    const links = navLinks as NavGroupModel[];

    const [isMobileSearchEngineVersion] = useMediaQuery(
        [`(max-width: ${searchEngineIsMobileSsrBreakpoint})`],
        {
            fallback: [isMobileRes ?? true],
        },
    );

    const ContactInfo = () => (
        <>
            <Text
                hideBelow={'xl'}
                fontSize={'sm'}
                mr={2}
                alignSelf={'flex-end'}
                dangerouslySetInnerHTML={{ __html: t('callUs') }}
            />
            <VStack fontSize={'xs'} align={'flex-start'} gap={1} hideBelow={'lg'}>
                <Link
                    href={urlHelpers.getPhoneNoHref(staticData.navigation.hotline)}
                    variant={'plain'}
                    fontSize={'sm'}
                    fontWeight={'bold'}
                    colorPalette={'gray'}
                >
                    <IconsLinear.Call mr={2} fontSize={'sm'} />
                    {staticData.navigation.hotline}
                </Link>
                <Text>
                    {staticData.navigation.openingDays}: {staticData.navigation.openingHours}
                </Text>
            </VStack>
        </>
    );

    const HeaderRightSection = () =>
        !isMobileRes ? (
            user ? (
                <>
                    <HStack gap={'gutterWidth'}>
                        <ContactInfo />
                    </HStack>
                    <Menu.Root
                        open={isProfileOpen}
                        onOpenChange={details =>
                            details.open ? handleOpenProfile() : handleCloseProfile()
                        }
                    >
                        <Menu.Trigger
                            as={Button}
                            _hover={{
                                bg: 'transparent',
                            }}
                            _expanded={{
                                bg: 'transparent',
                            }}
                            title={user.firstName || user.lastName || t('userMenu.defaultName')}
                            alignItems={'center'}
                        >
                            <Avatar src={user.avatar ?? undefined} />
                            <IconsLinear.ArrowDown2 fontSize={'sm'} />
                        </Menu.Trigger>
                        <Menu.Positioner>
                            <Menu.Content zIndex={5} color={'secondary.solid'} fontSize={'sm'}>
                                <Menu.Item
                                    _hover={{
                                        bg: 'secondary.50',
                                        textDecoration: 'none',
                                    }}
                                    _focus={{
                                        bg: 'secondary.50',
                                    }}
                                    asChild
                                    value="dashboard"
                                    cursor={'pointer'}
                                >
                                    <a href={getDashboardLink(user)}>{t('userMenu.goToPanel')}</a>
                                </Menu.Item>
                                <Menu.Item
                                    onClick={handleLogout}
                                    _hover={{
                                        bg: 'secondary.50',
                                    }}
                                    _focus={{
                                        bg: 'secondary.50',
                                    }}
                                    value="logout"
                                    cursor={'pointer'}
                                >
                                    {t('userMenu.logOut')}
                                </Menu.Item>
                            </Menu.Content>
                        </Menu.Positioner>
                    </Menu.Root>
                </>
            ) : (
                <HStack gap={'gutterWidth'}>
                    <ContactInfo />
                    <Button asChild colorPalette={'secondary'} variant={'outline'} ml={'2'}>
                        <a href={links[0].navLinks[1].url ?? '/'}>
                            <IconsLinear.Add />
                            {t('cta.addAccommodation')}
                        </a>
                    </Button>
                    <Button asChild colorPalette={'brand'} variant={'solid'}>
                        <a href={links[0].navLinks[0].url ?? '/'}>
                            <IconsLinear.User />
                            {t('cta.logIn')}
                        </a>
                    </Button>
                </HStack>
            )
        ) : null;

    return (
        <>
            {isHomePage && bannerData ? <MarketingBanner bannerData={bannerData} /> : null}
            <Box
                as={'header'}
                py={isOldMobileVariant ? 0 : 2}
                borderBottom={isGhostVariant ? undefined : '1px solid'}
                borderBottomColor={'gray.200'}
                mb={isGhostVariant ? '-headerOffset' : undefined}
            >
                <GridWrapper>
                    {isSimplified ? (
                        <AppHeaderLogo isGhostVariant={isGhostVariant} />
                    ) : (
                        <HStack as={GridItem} gridColumn={'1 / -1'} justify={'space-between'}>
                            <HStack gap={isMobileRes ? 0 : 'gutterWidth'}>
                                {isMobileRes ? (
                                    <NavigationMobile
                                        menuLabel={t('menu.menuLabel')}
                                        closeLabel={t('menu.closeLabel')}
                                        user={user}
                                        isGhostVariant={isGhostVariant}
                                    />
                                ) : null}
                                <AppHeaderLogo
                                    isGhostVariant={isGhostVariant}
                                    isLogoV7={isV7Theme}
                                />
                                {!isMobileRes ? (
                                    <Navigation triggerText={t('menu.planYourStay')} />
                                ) : null}
                            </HStack>
                            {showSearchEngine ? (
                                isMobileSearchEngineVersion ? (
                                    <Flex gap={4}>
                                        <SearchEngineWrapper />
                                        <HeaderRightSection />
                                    </Flex>
                                ) : (
                                    <>
                                        <SearchEngineWrapper />
                                        <HeaderRightSection />
                                    </>
                                )
                            ) : (
                                <HeaderRightSection />
                            )}
                        </HStack>
                    )}
                </GridWrapper>
            </Box>
        </>
    );
};
